import React, { useState, useEffect } from 'react';
import OnboardingHeader from '@/components/shared/OnboardingHeader';
import { useApp } from '@/contexts/AppContext';
import { useStrings } from '@/hooks/useStrings';
import { useScreenTransition } from '@/hooks/useScreenTransition';
import { safeWrite } from '@/utils/supabaseWrite';
import { hapticTap, hapticMilestone } from '@/utils/haptics';
import { playSqueezeChime, playReleaseChime, playSessionCompleteChime } from '@/utils/sound';
import { play as playMusic, stop as stopMusic } from '@/utils/musicCoach';

// ── Styles ───────────────────────────────────────────────────────────────

const s = {
  screen: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    background: 'var(--cream, #FAF6F1)',
    display: 'flex',
    flexDirection: 'column',
  },
  progressTrack: {
    flexShrink: 0,
    height: 3,
    width: '100%',
    background: 'var(--beige, #E8DDD0)',
    marginTop: 'var(--safe-top, env(safe-area-inset-top, 0px))',
  },
  progressFill: {
    height: '100%',
    background: 'var(--gold, #A8003F)',
    transition: 'width 300ms ease',
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: '32px 24px 16px',
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    width: '100%',
    maxWidth: 460,
    margin: '0 auto',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 28,
    fontWeight: 700,
    color: 'var(--dark-brown)',
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
    margin: '8px 0 18px',
  },
  subtitle: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 18,
    fontWeight: 400,
    color: 'var(--warm-brown, #8B7355)',
    lineHeight: 1.7,
    marginBottom: 24,
  },
  body: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 18,
    fontWeight: 400,
    color: 'var(--text, #3D2B1F)',
    lineHeight: 1.7,
    marginBottom: 24,
  },
  circleBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 0 32px',
    gap: 20,
  },
  cueText: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 14,
    fontStyle: 'italic',
    color: 'var(--gold, #A8003F)',
    textAlign: 'center',
    letterSpacing: '0.02em',
    minHeight: 22,
    margin: 0,
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 16,
  },
  option: {
    display: 'flex',
    alignItems: 'stretch',
    textAlign: 'left',
    width: '100%',
    background: 'var(--nude, #F5EDE3)',
    border: '1.5px solid var(--beige, #E8DDD0)',
    borderRadius: 'var(--radius-md, 12px)',
    padding: 0,
    cursor: 'pointer',
    transition: 'background 200ms ease, border-color 200ms ease',
    fontFamily: 'inherit',
    WebkitTapHighlightColor: 'transparent',
    overflow: 'hidden',
  },
  optionSelected: {
    background: 'var(--beige, #E8DDD0)',
    borderColor: 'var(--gold, #A8003F)',
  },
  optionBar: {
    width: 4,
    flexShrink: 0,
    background: 'transparent',
    transition: 'background 200ms ease',
  },
  optionBarSelected: {
    background: 'var(--gold, #A8003F)',
  },
  optionText: {
    flex: 1,
    padding: '16px 18px',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 18,
    fontWeight: 500,
    lineHeight: 1.55,
    color: 'var(--dark-brown, #5A3E2B)',
  },
  responseText: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 16,
    fontWeight: 400,
    color: 'var(--warm-brown, #8B7355)',
    lineHeight: 1.6,
    fontStyle: 'italic',
    margin: '8px 4px 16px',
  },
  bulletStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 8,
    marginBottom: 16,
    flex: 1,
  },
  bulletCard: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    background: 'var(--surface, #FFFFFF)',
    border: '1px solid var(--border, #E8DDD4)',
    borderRadius: 12,
    padding: '14px 16px',
  },
  bulletIcon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
  },
  bulletText: {
    flex: 1,
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 16,
    fontWeight: 400,
    color: 'var(--text, #3D2B1F)',
    lineHeight: 1.55,
  },
  optionIcon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    marginLeft: 18,
  },
  footer: {
    flexShrink: 0,
    background: 'var(--cream, #FAF6F1)',
    padding: '12px 24px calc(16px + env(safe-area-inset-bottom, 0px))',
  },
  cta: {
    display: 'block',
    width: '100%',
    maxWidth: 460,
    margin: '0 auto',
    padding: '18px 16px',
    border: 'none',
    borderRadius: 'var(--radius-md, 12px)',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: '0.02em',
    textAlign: 'center',
    transition: 'background 200ms ease, opacity 200ms ease',
    touchAction: 'manipulation',
  },
  ctaEnabled: {
    background: 'var(--gold, #A8003F)',
    color: '#fff',
    cursor: 'pointer',
    opacity: 1,
  },
  ctaDisabled: {
    background: 'var(--surface-high, #E8DDD0)',
    color: 'var(--text-secondary, #6B5A4A)',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
};

// ── Inline icons (stroke #A8003F, strokeWidth 1.6, round) ──────────────

const iconProps = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function IconWind() {
  return (
    <svg style={{ color: 'var(--primary)' }} {...iconProps} aria-hidden="true">
      <path d="M3 8h11a3 3 0 1 0-3-3" />
      <path d="M3 12h15a3 3 0 1 1-3 3" />
      <path d="M3 16h9" />
    </svg>
  );
}

function IconPerson() {
  return (
    <svg style={{ color: 'var(--primary)' }} {...iconProps} aria-hidden="true">
      <circle cx="12" cy="6" r="3" />
      <path d="M12 10v10" />
      <path d="M8 14h8" />
      <path d="M9 20l-1.5 1.5" />
      <path d="M15 20l1.5 1.5" />
    </svg>
  );
}

function IconEye() {
  return (
    <svg style={{ color: 'var(--primary)' }} {...iconProps} aria-hidden="true">
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" />
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  );
}

// ── Bullet card ─────────────────────────────────────────────────────────

function BulletCard({ icon, text, className, style }) {
  return (
    <div className={className} style={{ ...s.bulletCard, ...style }}>
      <span style={s.bulletIcon}>{icon}</span>
      <span style={s.bulletText}>{text}</span>
    </div>
  );
}

// ── Breath circle (inline SVG) ──────────────────────────────────────────
//
// Renders a rose outline circle driven by the CSS breath-cycle
// classes in motion.css. Phase -> className:
//   'in'    : .breathe-in     (1.5 → 0.4 over 4s — circle collapses)
//   'hold'  : .breathe-in     (forwards keeps the circle at 0.4)
//   'out'   : .breathe-out    (0.4 → 1.5 over 6s — circle blooms)
//   'pause' : .breathe-out    (forwards keeps the circle at 1.5)
//   'idle'  : no class        (static scale 1)
//
// Keeping the last completed animation class applied during hold /
// pause is what makes the forwards fill-mode stick — swapping to the
// empty string would snap the element back to its default transform.
// Cue text sits below the circle and uses key={phase} on the <p> so
// it remounts on every phase change, re-triggering the screen-enter
// fade-up and acting as a soft crossfade between cues.

function BreathCircle({ phase, cue }) {
  const circleClass =
    phase === 'in' || phase === 'hold' ? 'breathe-in'
    : phase === 'out' || phase === 'pause' ? 'breathe-out'
    : '';

  return (
    <div style={s.circleBox} aria-hidden="true">
      <svg width="180" height="180" viewBox="0 0 180 180" style={{ display: 'block', color: 'var(--primary)' }}>
        <g
          className={circleClass}
          style={{ transformOrigin: '90px 90px' }}
        >
          <circle
            cx="90"
            cy="90"
            r="48"
            fill="none"
            stroke="var(--gold, #A8003F)"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
        </g>
      </svg>
      <p key={phase} style={s.cueText} className="screen-enter">
        {cue}
      </p>
    </div>
  );
}

// ── Option card ─────────────────────────────────────────────────────────

function OptionCard({ label, selected, onSelect, icon }) {
  return (
    <button
      type="button"
      style={{ ...s.option, ...(selected ? s.optionSelected : null) }}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span style={{ ...s.optionBar, ...(selected ? s.optionBarSelected : null) }} aria-hidden="true" />
      {icon ? <span style={s.optionIcon}>{icon}</span> : null}
      <span style={s.optionText}>{label}</span>
    </button>
  );
}

// ── Main component ─────────────────────────────────────────────────────

export default function BreathingDiscovery({ onComplete, onClose, onExit }) {
  const { user } = useApp();
  const t = useStrings();
  const [currentScreen, setCurrentScreen] = useState(0);
  const [feltSomething, setFeltSomething] = useState(null); // true | false | null
  const [saving, setSaving] = useState(false);
  useScreenTransition(currentScreen);

  // Back on the first sub-screen dismisses the overlay (→ /home via
  // onClose threaded from OnboardingFlow). On timed sub-screens (1-3)
  // back returns the user to the welcome screen so they can re-enter
  // the breath choreography without it being already in flight.
  function handleBack() {
    if (saving) return;
    if (currentScreen === 0) {
      if (onClose) onClose();
      return;
    }
    setCurrentScreen(0);
  }

  // Skipping the breath exploration marks it skipped and advances the
  // outer OnboardingFlow. No breath-pattern answer is recorded.
  async function handleSkip() {
    if (saving) return;
    setSaving(true);
    try {
      let data = {};
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) data = JSON.parse(saved);
      } catch {}
      data.breathing_discovery_skipped = true;
      await safeWrite(user?.id, data);
      onComplete();
    } catch (err) {
      console.error('BreathingDiscovery skip save failed:', err);
      setSaving(false);
    }
  }

  // ── Breath choreography ──
  // Screen 1: inhale only (4s). Screen 2: exhale only (6s). Screen 3: full
  // asymmetric cycle (in 4s → hold 1s → out 6s → pause 1s). Each screen
  // auto-advances when its animation completes. Screen 0 and 4 are static.
  //
  // `breathPhase` drives both the CSS class on the circle (via
  // BreathCircle) and the text cue below it. 'idle' on non-breath
  // screens so the circle sits at its neutral rest scale.
  const [breathPhase, setBreathPhase] = useState('idle');

  useEffect(() => {
    if (currentScreen < 1 || currentScreen > 3) {
      setBreathPhase('idle');
      return undefined;
    }

    const timers = [];
    const after = (ms, fn) => timers.push(setTimeout(fn, ms));

    // Phase chimes (no-ops when sound is disabled): a soft rising tone
    // at the inhale, a gentle falling tone at the exhale. Keeps the
    // breath embodied even with the screen off, mirrors the player's
    // squeeze/release ladder.
    if (currentScreen === 1) {
      // Inhale only: in (4s) → advance
      setBreathPhase('in');
      playSqueezeChime();
      after(4000, () => setCurrentScreen(2));
    } else if (currentScreen === 2) {
      // Exhale only: out (6s) → advance
      setBreathPhase('out');
      playReleaseChime();
      after(6000, () => setCurrentScreen(3));
    } else if (currentScreen === 3) {
      // Full cycle: in (4s) → hold (1s) → out (6s) → pause (1s) → advance
      setBreathPhase('in');
      playSqueezeChime();
      after(4000,  () => setBreathPhase('hold'));
      after(5000,  () => { setBreathPhase('out'); playReleaseChime(); });
      after(11000, () => setBreathPhase('pause'));
      after(12000, () => setCurrentScreen(4));
    }

    return () => timers.forEach(clearTimeout);
  }, [currentScreen]);

  // Acknowledge the work when the choreography lands on screen 4.
  // Milestone haptic + soft completion chime fire once on entry; the
  // bloom in the JSX is mounted by the screen-4 conditional and
  // animates itself via .onboard-bloom.
  useEffect(() => {
    if (currentScreen !== 4) return;
    hapticMilestone();
    playSessionCompleteChime();
  }, [currentScreen]);

  // Soft ambient music under the breath choreography. play() is
  // gated on userProgress.music_enabled (default true) and silently
  // no-ops when the audio asset is missing, so this is safe to ship
  // before any audio file is committed. Starts when the user enters
  // the breath cycle (screen 1), stops on the welcome screen, the
  // acknowledgement, or unmount — so onboarding does not bleed music
  // into the next step.
  useEffect(() => {
    if (currentScreen >= 1 && currentScreen <= 3) {
      playMusic('flow').catch(() => {});
    } else {
      stopMusic();
    }
    return () => stopMusic();
  }, [currentScreen]);

  // ── Finish & persist ──
  async function finishAndContinue() {
    if (saving || feltSomething === null) return;
    hapticTap();
    setSaving(true);
    try {
      let data = {};
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) data = JSON.parse(saved);
      } catch {}
      data.breathing_discovery_complete = true;
      data.breathing_discovery_felt = feltSomething;
      await safeWrite(user?.id, data);
      onComplete();
    } catch (err) {
      console.error('BreathingDiscovery save failed:', err);
      setSaving(false);
    }
  }

  const progressPct = ((currentScreen + 1) / 5) * 100;

  const breathCueKey =
    breathPhase === 'in' ? 'bd_cue_in'
    : breathPhase === 'hold' ? 'bd_cue_hold'
    : breathPhase === 'out' ? 'bd_cue_out'
    : breathPhase === 'pause' ? 'bd_cue_pause'
    : null;
  const breathCue = breathCueKey ? t(breathCueKey) : '';

  return (
    <div style={s.screen}>
      <OnboardingHeader onBack={handleBack} onSkip={handleSkip} onExit={onExit} />

      {/* Progress bar */}
      <div
        style={s.progressTrack}
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={5}
        aria-valuenow={currentScreen + 1}
      >
        <div style={{ ...s.progressFill, width: `${progressPct}%` }} />
      </div>

      {/* Screen content */}
      <div style={s.scroll}>
        <div style={s.container}>
          {currentScreen === 0 && (
            <div key={currentScreen} className="screen-enter">
              <h1 style={s.title} className="screen-enter-child">{t('bd_welcome_title')}</h1>
              <div style={s.bulletStack} className="screen-enter-child">
                <BulletCard
                  icon={<IconWind />}
                  text={t('bd_welcome_bullet_1')}
                  className="stagger-item"
                  style={{ '--stagger-i': 0 }}
                />
                <BulletCard
                  icon={<IconPerson />}
                  text={t('bd_welcome_bullet_2')}
                  className="stagger-item"
                  style={{ '--stagger-i': 1 }}
                />
                <BulletCard
                  icon={<IconEye />}
                  text={t('bd_welcome_bullet_3')}
                  className="stagger-item"
                  style={{ '--stagger-i': 2 }}
                />
              </div>
            </div>
          )}

          {currentScreen === 1 && (
            <>
              <h1 style={s.title}>{t('bd_inhale_title')}</h1>
              <p style={s.body}>{t('bd_inhale_body')}</p>
              <BreathCircle phase={breathPhase} cue={breathCue} />
            </>
          )}

          {currentScreen === 2 && (
            <>
              <h1 style={s.title}>{t('bd_exhale_title')}</h1>
              <p style={s.body}>{t('bd_exhale_body')}</p>
              <BreathCircle phase={breathPhase} cue={breathCue} />
            </>
          )}

          {currentScreen === 3 && (
            <>
              <h1 style={s.title}>{t('bd_cycle_title')}</h1>
              <p style={s.body}>{t('bd_cycle_body')}</p>
              <BreathCircle phase={breathPhase} cue={breathCue} />
            </>
          )}

          {currentScreen === 4 && (
            <div key={currentScreen} className="screen-enter">
              <div style={{ position: 'relative' }}>
                {/* One-shot warm bloom acknowledging the breath the user
                    just took. Mounts with the screen, runs once, fades. */}
                <span className="onboard-bloom" aria-hidden="true" />
                <p style={{ ...s.cueText, marginBottom: 12 }} className="screen-enter-child">
                  {t('bd_ack_affirmation')}
                </p>
                <h1 style={s.title} className="screen-enter-child">{t('bd_ack_title')}</h1>
              </div>
              <p style={s.subtitle} className="screen-enter-child">{t('bd_ack_subtitle')}</p>
              <div style={s.options} className="screen-enter-child">
                <OptionCard
                  label={t('bd_ack_yes')}
                  selected={feltSomething === true}
                  onSelect={() => { hapticTap(); setFeltSomething(true); }}
                />
                <OptionCard
                  label={t('bd_ack_no')}
                  selected={feltSomething === false}
                  onSelect={() => { hapticTap(); setFeltSomething(false); }}
                />
              </div>
              {feltSomething !== null && (
                <p style={s.responseText}>
                  {feltSomething ? t('bd_ack_response_yes') : t('bd_ack_response_no')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer CTA (screens 0 and 4 only; 1-3 auto-advance) */}
      {(currentScreen === 0 || currentScreen === 4) && (
        <div style={s.footer}>
          {currentScreen === 0 && (
            <button
              type="button"
              style={{ ...s.cta, ...s.ctaEnabled }}
              onClick={() => { hapticTap(); setCurrentScreen(1); }}
            >
              {t('bd_welcome_cta')}
            </button>
          )}

          {currentScreen === 4 && (
            <button
              type="button" style={{ ...s.cta, ...(feltSomething !== null && !saving ? s.ctaEnabled : s.ctaDisabled) }}
              onClick={finishAndContinue}
              disabled={feltSomething === null || saving}
            >
              {t('bd_ack_cta')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
